import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchComplaintForm } from "../../../store/slices/complaintFormSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function ComplaintFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Mapping to 'currentForm' as defined in your slice
    const { currentForm, isLoading, error } = useSelector((state) => state.complaintForm || {});

    useEffect(() => {
        if (id) dispatch(fetchComplaintForm(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentForm) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentForm, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Complaint Form...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentForm) return <div className="p-8 text-center">No record found.</div>;

    const d = currentForm;

    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    const departments = ["Administration", "Kitchen", "Housekeeping", "Nursing/PRP", "Other"];

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 13px !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 40px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 20px !important; left: 20px !important; }
                .logo img { height: 70px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 30px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.7rem !important; font-weight: bold !important; }
                .header h6 { font-size: 1.1rem !important; text-transform: uppercase !important; margin-top: 5px !important; }

                .section { margin-bottom: 25px !important; border: 1px solid #000 !important; }
                .section-header { background: #f2f2f2 !important; padding: 8px 12px !important; font-weight: bold !important; border-bottom: 1px solid #000 !important; text-transform: uppercase !important; }
                .section-body { padding: 15px !important; }

                .info-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 15px !important; margin-bottom: 15px !important; }
                .underline { border-bottom: 1px solid #000 !important; font-weight: bold !important; padding: 0 5px !important; display: inline-block !important; min-width: 150px !important; }

                .dept-grid { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 10px !important; margin: 10px 0 !important; }
                .checkbox-row { display: flex !important; align-items: center !important; }
                .box { width: 16px !important; height: 16px !important; border: 1px solid #000 !important; margin-right: 8px !important; text-align: center !important; line-height: 14px !important; font-weight: bold !important; }
                .ticked { background: #000 !important; color: #fff !important; }

                .text-area { border: 1px solid #ccc !important; padding: 10px !important; min-height: 80px !important; margin-top: 5px !important; background: #fff !important; line-height: 1.5 !important; }

                .footer { margin-top: 50px !important; border-top: 1.5px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 11px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 40px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>COMPLAINT & RESOLUTION FORM</h6>
                </div>

                {/* Section 1: Complaint Details */}
                <div className="section">
                    <div className="section-header">1. Complaint Details</div>
                    <div className="section-body">
                        <div className="info-grid">
                            <div>Date of Complaint: <span className="underline">{fmtDate(d.date_of_complaint || d.dateOfComplaint)}</span></div>
                            <div>Case ID: <span className="underline">{d.case_id}</span></div>
                        </div>

                        <div style={{ fontWeight: 'bold', marginTop: '10px' }}>Department / Area of Concern:</div>
                        <div className="dept-grid">
                            {departments.map(dept => {
                                const isSelected = d.department === dept;
                                return (
                                    <div key={dept} className="checkbox-row">
                                        <div className={`box ${isSelected ? 'ticked' : ''}`}>{isSelected ? "✓" : ""}</div>
                                        <span>{dept === "Other" && d.other_department ? `Other: ${d.other_department}` : dept}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ marginTop: '15px' }}>
                            <strong>Description of Complaint:</strong>
                            <div className="text-area">{d.complaint_description || d.complaintDescription}</div>
                        </div>

                        <div style={{ marginTop: '15px' }}>
                            <strong>Suggested Solution:</strong>
                            <div className="text-area">{d.suggested_solution || d.suggestedSolution || "None provided."}</div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Resolution Details */}
                <div className="section">
                    <div className="section-header">2. Resolution Details (Admin Department)</div>
                    <div className="section-body">
                        <div>
                            <strong>Resolution Summary:</strong>
                            <div className="text-area" style={{ minHeight: '120px' }}>{d.resolution_summary || d.resolutionSummary || "Pending resolution..."}</div>
                        </div>
                        <div style={{ marginTop: '15px' }}>
                            Date of Resolution: <span className="underline">{fmtDate(d.date_of_resolution || d.dateOfResolution)}</span>
                        </div>
                    </div>
                </div>

                {/* Section 3: Acknowledgment */}
                <div className="section">
                    <div className="section-header">3. Acknowledgment & Sign-off</div>
                    <div className="section-body">
                        <div className="info-grid">
                            <div>Resolved By (Staff): <span className="underline">{d.resolved_by || d.resolvedBy}</span></div>
                            <div>Admin Rep: <span className="underline">{d.admin_representative || d.adminRepresentative}</span></div>
                        </div>
                        <div className="info-grid">
                            <div>Staff Resolution Date: <span className="underline">{fmtDate(d.staff_date_of_resolution || d.staffDateOfResolution)}</span></div>
                            <div>Admin Date: <span className="underline">{fmtDate(d.admin_date || d.adminDate)}</span></div>
                        </div>
                    </div>
                </div>

                {/* Signatures */}
                <div className="sig-row">
                    <div className="sig-box">Complainant Signature</div>
                    <div className="sig-box">Admin Dept Signature</div>
                    <div className="sig-box">Director Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}